import TemplateDto from '@/services/skills/Dto/TemplateDto';
import PageTemplateDto from '@/services/skills/Dto/PageTemplateDto';
import TemplateRequestDto from '@/services/skills/Dto/TemplateRequestDto';
import TemplatesApiRepository from '@/services/skills/TemplatesApiRepository';
import AgentSettingsService from '@/services/account/agentSettingsService';
import { v4 as uuidv4 } from 'uuid';
import {
    POST_PURCHASE_SKILL_ID,
    DEFAULT_DELAY_DAYS,
    parseDelayDays,
} from '@/components/Skills/LixsaSkills/PostPurchase/Utils/PostPurchaseConfig';
import { getDefaultPostPurchaseTemplate } from '@/components/Skills/LixsaSkills/PostPurchase/Utils/PostPurchaseDefaultTemplates';

const FLOW_TYPE = 'POST_PURCHASE';
// Es un mensaje para volver a vender, no un aviso del pedido: Meta lo exige como marketing.
const CATEGORY = 'MARKETING';

const state = () => ({
    templatesList: null,
    currentLanguage: 'en',
    currentTemplate: null,
    availableLanguages: [],
    /** Días tras la compra a los que sale el mensaje (`config_values.delay_days`). */
    delayDays: DEFAULT_DELAY_DAYS,
});

const formatLanguage = (language) => {
    if (!language) return '';
    const [base, region] = language.split('_');
    return region ? `${base.toLowerCase()}_${region.toUpperCase()}` : base.toLowerCase();
};

const getters = {
    getCurrentTemplate: (state) => state.currentTemplate,
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    getDelayDays: (state) => state.delayDays,
    /** El backend solo envía con una plantilla APPROVED: sin ninguna, la skill encendida no hace nada. */
    hasApprovedTemplate: (state) => (state.templatesList?.content || []).some((t) => t.status === 'APPROVED'),
    currentTemplateIsSavedInTemplatesList: (state) => {
        if (!state.currentTemplate || !state.templatesList) return false;

        return state.templatesList.content.some((template) => template.uuid === state.currentTemplate.uuid);
    },
    getCurrentTemplateStatus: (state) => {
        if (!state.currentTemplate) return null;

        return state.currentTemplate.status || 'NEW';
    },
};

const mutations = {
    SET_TEMPLATE_LIST(state, templatesList) {
        state.templatesList = templatesList;
    },
    SET_CURRENT_LANGUAGE(state, language) {
        state.currentLanguage = language;
    },
    SET_AVAILABLE_LANGUAGES(state, languages) {
        state.availableLanguages = languages;
    },
    SET_CURRENT_TEMPLATE(state, template) {
        state.currentTemplate = template;
    },
    SET_DELAY_DAYS(state, days) {
        state.delayDays = days;
    },
};

const actions = {
    async fetchTemplatesList({ commit }) {
        try {
            const templates = await TemplatesApiRepository.getTemplates(FLOW_TYPE);
            commit('SET_TEMPLATE_LIST', templates);
            return templates;
        } catch (error) {
            console.error('Error fetching post purchase templates:', error);
            throw error;
        }
    },

    async fetchCurrentTemplate({ commit, state, dispatch }) {
        const foundTemplate = state.templatesList?.content?.find(
            (template) => formatLanguage(template.language) === formatLanguage(state.currentLanguage),
        );
        if (foundTemplate) {
            commit('SET_CURRENT_TEMPLATE', new TemplateDto(foundTemplate));
            return;
        }

        await dispatch('fetchDefaultTemplate');
    },

    /**
     * Cuerpo por defecto de una plantilla nueva. Blindado como en SHIPPING_UPDATE: si el
     * backend no tiene TemplateVariables sembradas para POST_PURCHASE devuelve 200 sin
     * cuerpo, y sin el catálogo local el editor abriría en blanco.
     */
    async fetchDefaultTemplate({ commit, state }) {
        let contentExample = null;

        try {
            const templateVariables = await TemplatesApiRepository.getTemplateVariables(
                POST_PURCHASE_SKILL_ID,
                FLOW_TYPE,
                state.currentLanguage,
            );
            contentExample = templateVariables?.contentExample || null;
        } catch (error) {
            console.warn('Sin template-variables para POST_PURCHASE, uso el catálogo local:', error?.message);
        }

        commit(
            'SET_CURRENT_TEMPLATE',
            new TemplateDto.Builder()
                .withUuid(null)
                .withTemplateType('FLOW')
                .withFlowType(FLOW_TYPE)
                .withSkillId(POST_PURCHASE_SKILL_ID)
                .withLanguage(state.currentLanguage)
                .withBody(contentExample || getDefaultPostPurchaseTemplate(state.currentLanguage))
                .withVariables([])
                .build(),
        );
    },

    async fetchAvailableLanguages({ commit }) {
        try {
            const languages = await TemplatesApiRepository.getAvailableLanguages();
            commit('SET_AVAILABLE_LANGUAGES', languages);
        } catch (error) {
            console.error('Error fetching available languages:', error);
            throw error;
        }
    },

    /**
     * Crea o edita la plantilla del idioma actual. Siempre en la WABA por defecto
     * (`businessAccountId` null): el cron de recompra solo busca plantilla ahí, así que
     * una plantilla en otra línea no saldría nunca.
     */
    async updateTemplate({ state, dispatch }, { templateContent, accountId }) {
        try {
            const foundTemplate = state.currentTemplate?.uuid
                ? state.templatesList?.content?.find((template) => template.uuid === state.currentTemplate.uuid)
                : null;

            if (foundTemplate) {
                const templateRequest = new TemplateRequestDto(foundTemplate);
                templateRequest.body = templateContent;
                await TemplatesApiRepository.updateTemplate(foundTemplate.uuid, templateRequest);
            } else {
                const templateRequest = new TemplateRequestDto.Builder()
                    .withUuid(uuidv4())
                    .withAccountId(accountId)
                    .withBody(templateContent)
                    .withLanguage(formatLanguage(state.currentLanguage))
                    .withTemplateName('post_purchase')
                    .withCategory(CATEGORY)
                    .withType('TEXT')
                    .withTemplateType('FLOW')
                    .withFlowType(FLOW_TYPE)
                    .withSkillId(POST_PURCHASE_SKILL_ID)
                    .withChannelType('WHATSAPP')
                    .withBusinessAccountId(null)
                    .build();
                await TemplatesApiRepository.createTemplate(templateRequest);
            }
            await dispatch('fetchTemplatesList');
        } catch (error) {
            console.error('Error saving post purchase template:', error);
            throw error;
        }
    },

    async deleteTemplate({ commit, state }, templateUuid) {
        try {
            const foundTemplate = state.templatesList.content.find((template) => template.uuid === templateUuid);
            if (foundTemplate) {
                const templateRequest = new TemplateRequestDto(foundTemplate);
                await TemplatesApiRepository.deleteTemplate(templateUuid, templateRequest);

                const updatedContent = state.templatesList.content.filter((t) => t.uuid !== templateUuid);
                commit(
                    'SET_TEMPLATE_LIST',
                    new PageTemplateDto({
                        ...state.templatesList,
                        content: updatedContent,
                        numberOfElements: updatedContent.length,
                    }),
                );
            }
        } catch (error) {
            console.error('Error deleting post purchase template:', error);
            throw error;
        }
    },

    setCurrentLanguage({ commit }, language) {
        commit('SET_CURRENT_LANGUAGE', formatLanguage(language));
    },

    /** Lee `delay_days` de los `config_values` de la skill. */
    async fetchSkillConfig({ commit }) {
        try {
            const response = await AgentSettingsService.getAgentSettings();
            const skills = response.data?.skills || [];
            const skill = skills.find((s) => s.skill_id === POST_PURCHASE_SKILL_ID);
            commit('SET_DELAY_DAYS', parseDelayDays(skill?.config_values?.delay_days));
        } catch (error) {
            console.error('Error fetching post purchase skill config:', error);
        }
    },

    /**
     * Guarda los días por PATCH (`updateSkillConfigValues`), igual que el retardo de
     * Thanks Order: fusiona claves y no reescribe `enabled`. El cron no se toca: el
     * backend lee los días en cada ejecución.
     *
     * @param {number} days
     */
    async saveDelayDays({ commit }, days) {
        await AgentSettingsService.updateSkillConfigValues(POST_PURCHASE_SKILL_ID, { delay_days: String(days) });
        commit('SET_DELAY_DAYS', days);
    },

    async subscribeToTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTemplateEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    /**
     * El topic es el de TODAS las plantillas de la cuenta: solo se aplican los cambios
     * de las que ya están en la lista (las de recompra).
     */
    handleTemplateEvent({ commit, state }, event) {
        if (!event || !event.data || !state.templatesList) return;
        if (!['TemplateUpdated', 'TemplateScoringUpdated'].includes(event.name)) return;

        const source = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const index = state.templatesList.content.findIndex((t) => t.uuid === source.uuid);
        if (index === -1) return;

        const current = state.templatesList.content[index];
        const updated =
            event.name === 'TemplateScoringUpdated'
                ? { ...current, score: source.score }
                : {
                      ...current,
                      body: source.body ?? current.body,
                      status: source.status ?? current.status,
                      issues: source.issues ?? current.issues,
                      score: source.score ?? current.score,
                      dateUpdated: source.dateUpdated || source.date_updated || current.dateUpdated,
                  };

        const content = [...state.templatesList.content];
        content[index] = updated;
        commit('SET_TEMPLATE_LIST', { ...state.templatesList, content });

        if (state.currentTemplate?.uuid === updated.uuid) {
            commit('SET_CURRENT_TEMPLATE', new TemplateDto(updated));
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
